<template>
  <div class="overflow-clip">
    <div
      class="lg:rounded-b-3xl lg:p-6"
      :class="showTopThree ? 'lg:bg-cream' : ''"
    >
      <BaseModal
        :open="open"
        @close="open = false"
      >
        <VacancyFilterList
          v-if="!refreshFilters"
          :filters="state.vacancySearch.filters"
          :sectors="sectorFilters"
          form-id="vacancy-filter-mobile"
          @update-filters="handleSubmit"
          @close-modal="open = false"
          @reset-filters="resetFilters"
        />
      </BaseModal>
      <buttons-sticky-filter-button
        class="lg:hidden"
        @click="open = !open"
      />

      <header class="relative isolate py-10 lg:pt-40">
        <div
          class="absolute inset-0 -z-10 overflow-hidden rounded-3xl"
          :class="showTopThree ? 'bg-violet lg:bottom-48' : 'bg-dynamic -bottom-96 lg:-bottom-48'"
        >
          <patterns-paint class="absolute left-[-25] top-[-14%] h-full fill-purple lg:top-24 lg:h-auto" />
        </div>

        <div
          v-if="showTopThree"
          class="container"
        >
          <BaseChipTitle
            title="Jouw persoonlijke"
            chip="top 3 vacatures"
            bg-color="bg-white"
            text-color="text-violet"
            light
            center
            single-line
          />
          <div class="mt-10 flex justify-center gap-y-4 max-lg:flex-col lg:mt-16 lg:items-center lg:gap-x-6">
            <vacancy-tile
              v-for="(vacancy, index) in bestPaidVacancies"
              :key="vacancy.id"
              class="basis-1/3"
              :vacancy="vacancy"
              :index="index"
              :show-reviews="false"
              show-excerpt
              show-usps
            />
          </div>
        </div>
        <BaseChipTitle
          v-else
          title="Bekijk alle"
          chip="resultaten"
          light
          center
          no-margin
          single-line
        />
      </header>
    </div>
    <section
      id="top"
      class="relative py-8"
      :class="showTopThree ? 'lg:pt-12' : 'pt-0'"
    >
      <div class="container">
        <BaseChipTitle
          v-if="showTopThree"
          title="Bekijk alle"
          chip="resultaten"
          center
          single-line
        />
        <div class="gap-4 lg:grid lg:grid-cols-3">
          <aside class="hidden space-y-4 lg:col-span-1 lg:block">
            <div class="lg:card-border lg:card-base lg:p-6">
              <vacancy-filter-list
                v-if="!refreshFilters"
                :filters="state.vacancySearch.filters"
                :sectors="sectorFilters"
                form-id="vacancy-filter-desktop"
                @update-filters="handleSubmit"
                @reset-filters="resetFilters"
              />
            </div>
          </aside>
          <VacancyTable
            class="col-span-2"
            :loading="vacancySearchLoading"
            :vacancies="vacancies"
          />
          <BasePagination
            v-if="vacancies.length"
            class="col-span-2 col-start-2 flex flex-row items-center justify-between pt-8"
            :page="page"
            :side-dot-size="PAGE_MARGIN"
            :size="Math.ceil(totalVacancies / ITEMS_PER_PAGE)"
            @page-change="changePage"
          />
        </div>
      </div>
    </section>

    <section class="pt-20">
      <EmployerCarousel />
    </section>
  </div>
</template>

<script setup lang="ts">
import {useRouteQuery} from '@vueuse/router';
import {useGtm} from '@gtm-support/vue-gtm';
import sectorFilterQuery from '~/graphql/queries/sectorFilter.gql';
import vacancySearchQuery from '~/graphql/queries/vacancySearch.gql';
import type {Vacancy, VacancyFilters} from '~/graphql/graphql';
import type {SectorFilter} from '~/types';

useHead({
  link: [
    {rel: 'canonical', href: 'https://westerduin.nl/vacatures'},
  ],
});

definePageMeta({
  header: {
    absolute: true,
    transparent: true,
    light: true,
  },
  bg: 'bg-white',
  middleware: ['sofie-page'],
});

const route = useRoute();
const showTopThree = ref(route.query.topThree || false);

const ITEMS_PER_PAGE = 10;
const PAGE_MARGIN = 2;

const routeFilter = useRouteQuery<string>('filters');
const page = ref(0);

const filters = computed(() => routeFilter.value ? JSON.parse(routeFilter.value) : {sector: [], hours: []});
const state = ref({
  vacancySearch: {
    filters: routeFilter.value ? JSON.parse(routeFilter.value) : {},
    order: route.query?.order ?? (filters.value?.city ? '_geoPoint' : 'salary'),
    page,
    per_page: ITEMS_PER_PAGE,
  },
});

const open = ref(false);
const refreshFilters = ref(false);

const {result: vacancySearchResult, loading: vacancySearchLoading} = useQuery(vacancySearchQuery, state.value.vacancySearch);
const vacancies = computed<Vacancy[]>(() => vacancySearchResult.value?.vacancySearch?.data ?? []);
const totalVacancies = computed<number>(() => vacancySearchResult.value?.vacancySearch?.total ?? 0);

const {result: sectorFilterResult} = useQuery(sectorFilterQuery);
const sectorFilters = computed<SectorFilter[]>(() => sectorFilterResult.value?.sector ?? []);

const {result: bestPaidResult} = useQuery(vacancySearchQuery, {...state.value.vacancySearch, ...{order: 'salary_per_hour', order_direction: 'desc', per_page: 3}});
const bestPaidVacancies = computed<Vacancy[]>(() => bestPaidResult.value?.vacancySearch?.data ?? []);

const setFilters = (filters: VacancyFilters) => {
  state.value.vacancySearch.filters = filters;
  state.value.vacancySearch.order = route.query?.order ?? (filters?.city ? '_geoPoint' : 'salary');
  routeFilter.value = JSON.stringify(filters);
};

const handleSubmit = (data: VacancyFilters) => {
  setFilters(data);
  page.value = 0;
};

const resetFilters = () => {
  refreshFilters.value = true;
  setFilters({sector: [], hours: []});

  nextTick(() => {
    refreshFilters.value = false;
  });
};

const changePage = (newPage: number) => {
  page.value = newPage;
  const elem = document.getElementById('top');
  elem?.scrollIntoView({behavior: 'smooth', block: 'start'});
};

onMounted(() => {
  const gtm = useGtm();
  gtm?.trackEvent({event: 'view_item_list'});
});
</script>
